import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import "./DailyPurchases.css";

const PRESET_EMOJIS = ["📁", "🧹", "📦", "🔧", "☕", "💡", "🖨️", "🧴", "🛒", "🎨", "🏷️", "🛡️", "🚗", "👔", "✂️", "📌"];
const PRESET_COLORS = ["#2563eb", "#059669", "#7c3aed", "#d97706", "#dc2626", "#0284c7", "#4f46e5", "#0d9488", "#e11d48", "#1e293b"];

const GestionFamillesAchats = () => {
  const currentUser = useSelector((state) => state.AuthReducer?.user);
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "useradmin";
  const isController = currentUser?.role === "controleur" || (currentUser?.fonction && currentUser.fonction.toLowerCase().includes("control"));

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPendingOnly, setFilterPendingOnly] = useState(false);

  // Modale Famille
  const [showCatModal, setShowCatModal] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catName, setCatName] = useState("");
  const [catIcon, setCatIcon] = useState("📁");
  const [catColor, setCatColor] = useState("#2563eb");
  const [catDesc, setCatDesc] = useState("");

  // Modale Sous-Famille / Article (Création ou Correction)
  const [showSubModal, setShowSubModal] = useState(false);
  const [targetCat, setTargetCat] = useState(null);
  const [editingSubIdx, setEditingSubIdx] = useState(null);
  const [subName, setSubName] = useState("");
  const [subUnit, setSubUnit] = useState("pièce");
  const [subPrice, setSubPrice] = useState(0);
  const [subDesc, setSubDesc] = useState("");
  const [selectedTargetCatId, setSelectedTargetCatId] = useState("");

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/daily-purchases/categories");
      setCategories(res.data || []);
    } catch (err) {
      console.error("Erreur chargement familles:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Calcul des statistiques globales
  const totalSubCategories = useMemo(() => {
    return categories.reduce((sum, cat) => sum + (cat.subCategories?.length || 0), 0);
  }, [categories]);

  // Compteur d'éléments en attente d'approbation
  const pendingCategoriesCount = useMemo(() => {
    return categories.filter((c) => c.isApprovedByAdmin === false).length;
  }, [categories]);

  const pendingSubsCount = useMemo(() => {
    return categories.reduce((sum, cat) => {
      const pendingInCat = (cat.subCategories || []).filter((s) => s.isApprovedByAdmin === false).length;
      return sum + pendingInCat;
    }, 0);
  }, [categories]);

  const totalPending = pendingCategoriesCount + pendingSubsCount;

  // Filtrage des familles et articles
  const filteredCategories = useMemo(() => {
    let list = categories;

    if (filterPendingOnly) {
      list = list
        .map((cat) => {
          const isCatPending = cat.isApprovedByAdmin === false;
          const pendingSubs = (cat.subCategories || []).filter((s) => s.isApprovedByAdmin === false);
          if (isCatPending || pendingSubs.length > 0) {
            return {
              ...cat,
              subCategories: isCatPending ? cat.subCategories : pendingSubs
            };
          }
          return null;
        })
        .filter(Boolean);
    }

    if (!searchTerm.trim()) return list;
    const term = searchTerm.toLowerCase();

    return list
      .map((cat) => {
        const matchesCatName = cat.name.toLowerCase().includes(term);
        const matchedSubs = (cat.subCategories || []).filter(
          (sub) =>
            sub.name.toLowerCase().includes(term) ||
            (sub.unit && sub.unit.toLowerCase().includes(term)) ||
            (sub.defaultDescription && sub.defaultDescription.toLowerCase().includes(term))
        );

        if (matchesCatName) {
          return cat;
        } else if (matchedSubs.length > 0) {
          return { ...cat, subCategories: matchedSubs };
        }
        return null;
      })
      .filter(Boolean);
  }, [categories, searchTerm, filterPendingOnly]);

  // Ouvrir modal ajout famille
  const handleOpenAddCat = () => {
    setEditingCat(null);
    setCatName("");
    setCatIcon("📁");
    setCatColor("#2563eb");
    setCatDesc("");
    setShowCatModal(true);
  };

  // Ouvrir modal édition famille
  const handleOpenEditCat = (cat) => {
    setEditingCat(cat);
    setCatName(cat.name);
    setCatIcon(cat.icon || "📦");
    setCatColor(cat.color || "#2563eb");
    setCatDesc(cat.description || "");
    setShowCatModal(true);
  };

  // Sauvegarde famille
  const handleSaveCat = async () => {
    if (!catName.trim()) {
      alert("Le nom de la famille est requis.");
      return;
    }

    try {
      if (editingCat) {
        await axios.put(`/api/daily-purchases/categories/${editingCat._id}`, {
          name: catName.trim(),
          icon: catIcon,
          color: catColor,
          description: catDesc,
          subCategories: editingCat.subCategories,
          isApprovedByAdmin: isAdmin ? true : (editingCat.isApprovedByAdmin || false),
          approvedByName: isAdmin ? currentUser?.name : editingCat.approvedByName
        });
      } else {
        await axios.post("/api/daily-purchases/categories", {
          name: catName.trim(),
          icon: catIcon,
          color: catColor,
          description: catDesc,
          subCategories: [],
          createdByRole: isAdmin ? "admin" : "controleur",
          createdByName: currentUser?.name || (isAdmin ? "Administrateur" : "Contrôleur"),
          isApprovedByAdmin: isAdmin ? true : false
        });
      }
      setShowCatModal(false);
      fetchCategories();
    } catch (err) {
      console.error("Erreur sauvegarde famille:", err);
      alert(err.response?.data?.error || "Erreur sauvegarde.");
    }
  };

  // Approbation directe d'une famille par l'Admin
  const handleApproveCat = async (cat) => {
    try {
      const approvedSubs = (cat.subCategories || []).map((s) => ({
        ...s,
        isApprovedByAdmin: true,
        approvedAt: new Date(),
        approvedByName: currentUser?.name || "Administrateur"
      }));

      await axios.put(`/api/daily-purchases/categories/${cat._id}`, {
        ...cat,
        isApprovedByAdmin: true,
        approvedByName: currentUser?.name || "Administrateur",
        subCategories: approvedSubs
      });
      fetchCategories();
    } catch (err) {
      console.error("Erreur approbation famille:", err);
      alert("Erreur lors de l'approbation.");
    }
  };

  // Suppression famille
  const handleDeleteCat = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette famille et tous ses articles ?")) return;
    try {
      await axios.delete(`/api/daily-purchases/categories/${id}`);
      fetchCategories();
    } catch (err) {
      console.error("Erreur suppression:", err);
    }
  };

  // Ouvrir modal ajout sous-famille / article
  const handleOpenAddSub = (cat) => {
    setTargetCat(cat);
    setEditingSubIdx(null);
    setSelectedTargetCatId(cat._id);
    setSubName("");
    setSubUnit("pièce");
    setSubPrice(0);
    setSubDesc("");
    setShowSubModal(true);
  };

  // Ouvrir modal modification / correction sous-famille / article
  const handleOpenEditSub = (cat, sub, idx) => {
    setTargetCat(cat);
    setEditingSubIdx(idx);
    setSelectedTargetCatId(cat._id);
    setSubName(sub.name);
    setSubUnit(sub.unit || "pièce");
    setSubPrice(sub.estimatedPrice || 0);
    setSubDesc(sub.defaultDescription || "");
    setShowSubModal(true);
  };

  // Sauvegarde sous-famille / article
  const handleSaveSub = async () => {
    if (!subName.trim() || !targetCat) {
      alert("La désignation de l'article est requise.");
      return;
    }

    try {
      // Cas 1 : Modification d'un article existant
      if (editingSubIdx !== null) {
        const destCat = categories.find((c) => c._id === selectedTargetCatId) || targetCat;
        const subData = {
          ...(targetCat.subCategories[editingSubIdx] || {}),
          name: subName.trim(),
          unit: subUnit.trim() || "pièce",
          estimatedPrice: Number(subPrice) || 0,
          defaultDescription: subDesc.trim(),
          isActive: true,
          isApprovedByAdmin: isAdmin ? true : (targetCat.subCategories[editingSubIdx]?.isApprovedByAdmin || false),
          approvedByName: isAdmin ? (currentUser?.name || "Administrateur") : ""
        };

        if (destCat._id === targetCat._id) {
          // Même catégorie
          const updatedSubs = [...targetCat.subCategories];
          updatedSubs[editingSubIdx] = subData;
          await axios.put(`/api/daily-purchases/categories/${targetCat._id}`, {
            ...targetCat,
            subCategories: updatedSubs
          });
        } else {
          // Déplacement vers une autre famille (correction de famille)
          const newSourceSubs = targetCat.subCategories.filter((_, i) => i !== editingSubIdx);
          await axios.put(`/api/daily-purchases/categories/${targetCat._id}`, {
            ...targetCat,
            subCategories: newSourceSubs
          });

          const newDestSubs = [...(destCat.subCategories || []), subData];
          await axios.put(`/api/daily-purchases/categories/${destCat._id}`, {
            ...destCat,
            subCategories: newDestSubs
          });
        }
      } else {
        // Cas 2 : Nouvel article
        const newSub = {
          name: subName.trim(),
          unit: subUnit.trim() || "pièce",
          estimatedPrice: Number(subPrice) || 0,
          defaultDescription: subDesc.trim(),
          isActive: true,
          isApprovedByAdmin: isAdmin ? true : false,
          createdByRole: isAdmin ? "admin" : "controleur",
          createdByName: currentUser?.name || (isAdmin ? "Administrateur" : "Contrôleur")
        };

        const updatedSubs = [...(targetCat.subCategories || []), newSub];
        await axios.put(`/api/daily-purchases/categories/${targetCat._id}`, {
          ...targetCat,
          subCategories: updatedSubs
        });
      }

      setShowSubModal(false);
      fetchCategories();
    } catch (err) {
      console.error("Erreur sauvegarde sous-famille:", err);
      alert("Erreur lors de la sauvegarde de l'article.");
    }
  };

  // Approbation directe d'un article par l'Admin
  const handleApproveSub = async (cat, subIdx) => {
    try {
      const updatedSubs = [...cat.subCategories];
      updatedSubs[subIdx] = {
        ...updatedSubs[subIdx],
        isApprovedByAdmin: true,
        approvedAt: new Date(),
        approvedByName: currentUser?.name || "Administrateur"
      };

      await axios.put(`/api/daily-purchases/categories/${cat._id}`, {
        ...cat,
        subCategories: updatedSubs
      });
      fetchCategories();
    } catch (err) {
      console.error("Erreur approbation article:", err);
      alert("Erreur lors de l'approbation de l'article.");
    }
  };

  // Suppression d'un article
  const handleDeleteSub = async (cat, subIdx) => {
    if (!window.confirm("Voulez-vous supprimer cet article de la famille ?")) return;
    try {
      const updatedSubs = cat.subCategories.filter((_, i) => i !== subIdx);
      await axios.put(`/api/daily-purchases/categories/${cat._id}`, {
        ...cat,
        subCategories: updatedSubs
      });
      fetchCategories();
    } catch (err) {
      console.error("Erreur suppression sous-famille:", err);
    }
  };

  return (
    <div>
      {/* ── HERO BANNER STATS & ACTIONS ── */}
      <div className="dp-fam-hero">
        <div className="dp-fam-hero-info">
          <h3>
            <span>⚙️</span> Nomenclature & Gestion des Familles d'Achats
          </h3>
          <p>
            {isAdmin
              ? "Gérez les familles et articles d'achats, validez les propositions soumises par le contrôleur et corrigez les libellés, familles et unités en cas d'erreur de saisie."
              : "Consultez le catalogue officiel, proposez de nouvelles familles ou ajoutez des articles. Vos ajouts seront validés et vérifiés par l'Administrateur."}
          </p>
        </div>

        <div className="dp-fam-hero-stats">
          <div className="dp-fam-stat-pill">
            <span style={{ fontSize: "1.4rem" }}>📁</span>
            <div>
              <div className="dp-fam-stat-pill-num">{categories.length}</div>
              <div className="dp-fam-stat-pill-label">Familles</div>
            </div>
          </div>

          <div className="dp-fam-stat-pill">
            <span style={{ fontSize: "1.4rem" }}>📦</span>
            <div>
              <div className="dp-fam-stat-pill-num" style={{ color: "#4ade80" }}>
                {totalSubCategories}
              </div>
              <div className="dp-fam-stat-pill-label">Articles Types</div>
            </div>
          </div>

          {totalPending > 0 && (
            <div
              className="dp-fam-stat-pill"
              style={{
                background: "rgba(245, 158, 11, 0.15)",
                border: "1px solid #f59e0b"
              }}
            >
              <span style={{ fontSize: "1.4rem" }}>⏳</span>
              <div>
                <div className="dp-fam-stat-pill-num" style={{ color: "#fbbf24" }}>
                  {totalPending}
                </div>
                <div className="dp-fam-stat-pill-label" style={{ color: "#fde68a" }}>
                  À Approuver
                </div>
              </div>
            </div>
          )}

          <button
            className="dp-btn dp-btn-primary"
            style={{
              padding: "12px 20px",
              boxShadow: "0 4px 14px rgba(37, 99, 235, 0.4)",
              fontWeight: "800"
            }}
            onClick={handleOpenAddCat}
          >
            ➕ Nouvelle Famille
          </button>
        </div>
      </div>

      {/* ── BANNIÈRE D'ALERTE EN ATTENTE D'APPROBATION (ADMIN) ── */}
      {totalPending > 0 && (
        <div
          style={{
            background: "#fffbeb",
            border: "1.5px solid #fde68a",
            borderLeft: "6px solid #f59e0b",
            padding: "14px 20px",
            borderRadius: "12px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px"
          }}
        >
          <div>
            <div style={{ fontWeight: "800", color: "#92400e", fontSize: "0.95rem" }}>
              🔔 {totalPending} élément(s) en attente d'approbation et vérification
            </div>
            <div style={{ fontSize: "0.85rem", color: "#b45309", marginTop: "2px" }}>
              {isAdmin
                ? "Le Contrôleur a ajouté des familles ou articles. Vérifiez la désignation, l'orthographe, l'unité et la famille associée avant de les valider."
                : "Vos propositions d'articles ou de familles ont bien été transmises et sont actuellement en attente de validation par l'Administrateur."}
            </div>
          </div>

          <button
            type="button"
            className="dp-btn"
            style={{
              background: filterPendingOnly ? "#d97706" : "#fef3c7",
              color: filterPendingOnly ? "#ffffff" : "#92400e",
              border: "1px solid #f59e0b",
              fontWeight: "800",
              fontSize: "0.82rem"
            }}
            onClick={() => setFilterPendingOnly(!filterPendingOnly)}
          >
            {filterPendingOnly ? "👁️ Afficher Tout le Catalogue" : "🔍 Filtrer les Éléments à Approuver"}
          </button>
        </div>
      )}

      {/* ── BARRE DE RECHERCHE RAPIDE ── */}
      <div
        style={{
          background: "#ffffff",
          padding: "16px 20px",
          borderRadius: "14px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          gap: "14px",
          flexWrap: "wrap"
        }}
      >
        <div style={{ flex: 1, minWidth: "260px", position: "relative" }}>
          <span
            style={{
              position: "absolute",
              left: "14px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
              fontSize: "1rem"
            }}
          >
            🔍
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Rechercher une famille ou un article (ex: Fourniture, Stylo, Agrafe, Javel, Café...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              paddingLeft: "42px",
              borderRadius: "10px",
              border: "1px solid #cbd5e1",
              fontSize: "0.92rem"
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                color: "#94a3b8",
                cursor: "pointer"
              }}
            >
              ✕
            </button>
          )}
        </div>

        {searchTerm && (
          <div style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: "600" }}>
            Résultats : <strong>{filteredCategories.length} famille(s)</strong>
          </div>
        )}
      </div>

      {/* ── LISTE DES FAMILLES ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px", color: "#64748b" }}>
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2 font-weight-bold">Chargement de la configuration des familles...</p>
        </div>
      ) : filteredCategories.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "50px 20px",
            background: "white",
            borderRadius: "16px",
            border: "1px dashed #cbd5e1"
          }}
        >
          <span style={{ fontSize: "3.5rem" }}>📁</span>
          <h4 style={{ fontWeight: "800", marginTop: "12px", color: "#0f172a" }}>
            Aucune famille trouvée
          </h4>
          <p style={{ color: "#64748b" }}>
            {searchTerm
              ? "Aucun résultat pour cette recherche."
              : "Commencez par créer votre première famille de dépenses d'achats quotidiens."}
          </p>
          <button className="dp-btn dp-btn-primary mt-2" onClick={handleOpenAddCat}>
            ➕ Créer une Famille
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {filteredCategories.map((cat) => {
            const themeColor = cat.color || "#2563eb";
            const subCount = cat.subCategories?.length || 0;
            const isCatPending = cat.isApprovedByAdmin === false;

            return (
              <div
                key={cat._id}
                className="dp-fam-card"
                style={{
                  borderTop: `4px solid ${isCatPending ? "#f59e0b" : themeColor}`,
                  boxShadow: isCatPending ? "0 0 0 2px #fde68a" : "0 4px 15px rgba(0,0,0,0.05)"
                }}
              >
                {/* Entête Famille */}
                <div
                  className="dp-fam-header"
                  style={{
                    background: isCatPending
                      ? "linear-gradient(135deg, #fffbeb 0%, #ffffff 100%)"
                      : `linear-gradient(135deg, ${themeColor}0D 0%, #ffffff 100%)`
                  }}
                >
                  <div className="dp-fam-title-group">
                    <div
                      className="dp-fam-icon-avatar"
                      style={{
                        background: isCatPending ? "#f59e0b" : themeColor,
                        color: "white"
                      }}
                    >
                      {cat.icon || "📦"}
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <h4 className="dp-fam-name" style={{ margin: 0 }}>
                          {cat.name}
                        </h4>
                        <span
                          className="dp-fam-count-badge"
                          style={{
                            background: `${themeColor}15`,
                            color: themeColor,
                            fontWeight: "800"
                          }}
                        >
                          {subCount} article{subCount > 1 ? "s" : ""}
                        </span>

                        {isCatPending ? (
                          <span
                            style={{
                              background: "#fef3c7",
                              color: "#b45309",
                              border: "1px solid #fde68a",
                              fontSize: "0.72rem",
                              fontWeight: "800",
                              padding: "2px 8px",
                              borderRadius: "6px"
                            }}
                          >
                            ⏳ En attente validation Admin (Ajouté par {cat.createdByName || "Contrôleur"})
                          </span>
                        ) : (
                          <span
                            style={{
                              background: "#f0fdf4",
                              color: "#166534",
                              border: "1px solid #bbf7d0",
                              fontSize: "0.72rem",
                              fontWeight: "700",
                              padding: "2px 8px",
                              borderRadius: "6px"
                            }}
                          >
                            ✅ Famille Validée
                          </span>
                        )}
                      </div>
                      {cat.description && (
                        <div className="dp-fam-desc">{cat.description}</div>
                      )}
                    </div>
                  </div>

                  <div className="dp-fam-actions">
                    {isAdmin && isCatPending && (
                      <button
                        className="dp-btn-action"
                        style={{ background: "#16a34a", color: "#ffffff", fontWeight: "800" }}
                        onClick={() => handleApproveCat(cat)}
                        title="Approuver et valider cette famille"
                      >
                        ✅ Approuver la Famille
                      </button>
                    )}

                    <button
                      className="dp-btn-action dp-btn-action-primary"
                      onClick={() => handleOpenAddSub(cat)}
                    >
                      ➕ Ajouter un article
                    </button>
                    <button
                      className="dp-btn-action dp-btn-action-secondary"
                      onClick={() => handleOpenEditCat(cat)}
                      title="Modifier ou corriger le nom/icône de la famille"
                    >
                      ✏️ {isAdmin ? "Corriger" : "Modifier"}
                    </button>
                    <button
                      className="dp-btn-action dp-btn-action-danger"
                      onClick={() => handleDeleteCat(cat._id)}
                      title="Supprimer la famille"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Corps / Grille des articles / sous-familles */}
                <div className="dp-fam-body">
                  <div className="dp-subs-grid">
                    {/* Tuile rapide d'ajout */}
                    <div
                      className="dp-sub-card-add"
                      onClick={() => handleOpenAddSub(cat)}
                    >
                      <span style={{ fontSize: "1.2rem", color: themeColor }}>➕</span>
                      <span>Nouvel Article</span>
                    </div>

                    {(cat.subCategories || []).map((sub, idx) => {
                      const isSubPending = sub.isApprovedByAdmin === false;

                      return (
                        <div
                          key={idx}
                          className="dp-sub-card"
                          style={{
                            border: isSubPending ? "1.5px solid #f59e0b" : "1px solid #e2e8f0",
                            background: isSubPending ? "#fffdf5" : "#ffffff"
                          }}
                        >
                          <div className="dp-sub-info">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "6px" }}>
                              <div className="dp-sub-name" title={sub.name} style={{ fontWeight: "800" }}>
                                {sub.name}
                              </div>
                            </div>

                            {isSubPending && (
                              <div style={{ margin: "3px 0" }}>
                                <span
                                  style={{
                                    fontSize: "0.68rem",
                                    fontWeight: "800",
                                    color: "#b45309",
                                    background: "#fef3c7",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    display: "inline-block"
                                  }}
                                >
                                  ⏳ À approuver ({sub.createdByName || "Contrôleur"})
                                </span>
                              </div>
                            )}

                            <div className="dp-sub-meta">
                              <span className="dp-sub-unit-pill">
                                📏 {sub.unit || "pièce"}
                              </span>
                              <span className="dp-sub-price-pill">
                                💰 {(sub.estimatedPrice || 0).toFixed(3)} DT
                              </span>
                            </div>
                            {sub.defaultDescription && (
                              <div
                                style={{
                                  fontSize: "0.72rem",
                                  color: "#94a3b8",
                                  marginTop: "3px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis"
                                }}
                              >
                                {sub.defaultDescription}
                              </div>
                            )}
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "8px", borderTop: "1px solid #f1f5f9", paddingTop: "6px" }}>
                            {isAdmin && isSubPending && (
                              <button
                                type="button"
                                style={{
                                  background: "#16a34a",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "6px",
                                  padding: "3px 8px",
                                  fontSize: "0.72rem",
                                  fontWeight: "800",
                                  cursor: "pointer"
                                }}
                                onClick={() => handleApproveSub(cat, idx)}
                                title="Approuver cet article"
                              >
                                ✅ Approuver
                              </button>
                            )}

                            <button
                              type="button"
                              style={{
                                background: "#f1f5f9",
                                color: "#334155",
                                border: "1px solid #cbd5e1",
                                borderRadius: "6px",
                                padding: "3px 8px",
                                fontSize: "0.72rem",
                                fontWeight: "700",
                                cursor: "pointer"
                              }}
                              onClick={() => handleOpenEditSub(cat, sub, idx)}
                              title="Corriger l'orthographe, l'unité, le prix ou la famille"
                            >
                              ✏️ Corriger
                            </button>

                            <button
                              className="dp-sub-delete-btn"
                              style={{ marginLeft: "auto", position: "static" }}
                              onClick={() => handleDeleteSub(cat, idx)}
                              title="Supprimer cet article"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── MODALE CRÉATION / ÉDITION FAMILLE ── */}
      <Modal show={showCatModal} onHide={() => setShowCatModal(false)} size="lg" centered>
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
            color: "white"
          }}
        >
          <Modal.Title style={{ fontWeight: "800", fontSize: "1.15rem" }}>
            {editingCat ? "✏️ Modifier / Corriger la Famille d'Achat" : "➕ Créer une Famille d'Achat"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "24px" }}>
          <div className="mb-3">
            <label className="form-label font-weight-bold">
              Nom de la Famille *
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Ex: Fournitures de bureau, Produits d'entretien, Cafétéria..."
              value={catName}
              onChange={(e) => setCatName(e.target.value)}
              style={{ borderRadius: "10px", padding: "10px 14px", fontWeight: "600" }}
            />
          </div>

          <div className="mb-3">
            <label className="form-label font-weight-bold">
              Icône Emoji ({catIcon})
            </label>
            <div className="dp-preset-emojis">
              {PRESET_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className={`dp-emoji-btn ${catIcon === emoji ? "active" : ""}`}
                  onClick={() => setCatIcon(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label font-weight-bold">Couleur Thématique</label>
            <div className="dp-preset-colors">
              {PRESET_COLORS.map((col) => (
                <div
                  key={col}
                  className={`dp-color-swatch ${catColor === col ? "active" : ""}`}
                  style={{ background: col }}
                  onClick={() => setCatColor(col)}
                />
              ))}
            </div>
          </div>

          <div className="mb-2">
            <label className="form-label font-weight-bold">
              Description / Notes
            </label>
            <textarea
              className="form-control"
              rows="2"
              placeholder="Description des articles généralement inclus dans cette famille..."
              value={catDesc}
              onChange={(e) => setCatDesc(e.target.value)}
              style={{ borderRadius: "10px", resize: "none" }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: "#f8fafc" }}>
          <Button variant="secondary" onClick={() => setShowCatModal(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            style={{ fontWeight: "800", padding: "8px 20px" }}
            onClick={handleSaveCat}
          >
            💾 {editingCat ? "Enregistrer les Corrections" : "Enregistrer la Famille"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── MODALE AJOUT / CORRECTION ARTICLE ── */}
      <Modal show={showSubModal} onHide={() => setShowSubModal(false)} size="lg" centered>
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
            color: "white"
          }}
        >
          <Modal.Title style={{ fontWeight: "800", fontSize: "1.15rem" }}>
            {editingSubIdx !== null
              ? `✏️ Modifier / Corriger l'Article « ${subName} »`
              : `➕ Ajouter un Nouvel Article dans « ${targetCat?.name} »`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "24px" }}>
          {/* Sélection de la famille parente (pour pouvoir corriger/déplacer une famille en cas d'erreur) */}
          <div className="mb-3">
            <label className="form-label font-weight-bold">
              📁 Famille de Rattachement
            </label>
            <select
              className="form-control"
              value={selectedTargetCatId}
              onChange={(e) => setSelectedTargetCatId(e.target.value)}
              style={{ borderRadius: "10px", fontWeight: "700" }}
            >
              {categories.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.icon || "📦"} {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label font-weight-bold">
              Désignation de l'Article (Orthographe & Nom Précis) *
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Ex: Agrafe 24/6, Stylo bille bleu, Rouleau caisse 80mm, Javel 5L..."
              value={subName}
              onChange={(e) => setSubName(e.target.value)}
              style={{ borderRadius: "10px", padding: "10px 14px", fontWeight: "600" }}
            />
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <label className="form-label font-weight-bold">Unité de Mesure</label>
              <select
                className="form-control"
                value={subUnit}
                onChange={(e) => setSubUnit(e.target.value)}
                style={{ borderRadius: "10px", fontWeight: "600" }}
              >
                <option value="pièce">pièce</option>
                <option value="boîte">boîte</option>
                <option value="rouleau">rouleau</option>
                <option value="paquet">paquet</option>
                <option value="ramette">ramette</option>
                <option value="litre">litre</option>
                <option value="bidon">bidon</option>
                <option value="kg">kg</option>
                <option value="carton">carton</option>
                <option value="lot 100">lot 100</option>
                <option value="pack">pack</option>
                <option value="paire">paire</option>
              </select>
            </div>

            <div className="col-6">
              <label className="form-label font-weight-bold">
                Prix Réf. Estimé (DT)
              </label>
              <input
                type="number"
                step="0.001"
                min="0"
                className="form-control"
                value={subPrice}
                onChange={(e) => setSubPrice(e.target.value)}
                style={{ borderRadius: "10px", fontWeight: "700" }}
              />
            </div>
          </div>

          <div className="mb-2">
            <label className="form-label font-weight-bold">
              Spécification / Description
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Ex: Boîte de 1000 agrafes standards, papier thermique 80x80..."
              value={subDesc}
              onChange={(e) => setSubDesc(e.target.value)}
              style={{ borderRadius: "10px" }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: "#f8fafc" }}>
          <Button variant="secondary" onClick={() => setShowSubModal(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            style={{ fontWeight: "800", padding: "8px 20px" }}
            onClick={handleSaveSub}
          >
            💾 {editingSubIdx !== null ? "Enregistrer les Corrections" : "Ajouter cet Article"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GestionFamillesAchats;
